import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Row, Col, Table, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { workoutAPI } from '../services/workoutAPI';
import {
  calculateAllZones,
  formatMarathonTime,
  RUN_TYPE_ORDER
} from '../utils/marathonCalculations';

function PfitzingerCalculator() {
  // Input state
  const [goalTime, setGoalTime] = useState('');
  const [age, setAge] = useState('');

  // Results state
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Save state
  const [saveName, setSaveName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Saved calculations state
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Fetch saved calculations on mount
  const fetchSavedCalculations = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await workoutAPI.fetchMarathonCalculations();
      setSavedCalculations(data);
      setLoadError(null);
    } catch (err) {
      console.error('Error fetching calculations:', err);
      setLoadError('Failed to load saved calculations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSavedCalculations();
  }, [fetchSavedCalculations]);

  // Handle calculate
  const handleCalculate = () => {
    setError(null);
    setSaveSuccess(false);

    const result = calculateAllZones(goalTime, age);

    if (result.error) {
      setError(result.error);
      setResults(null);
    } else {
      setResults(result);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!results) return;

    const name = saveName.trim() || `Calculation ${new Date().toLocaleDateString()}`;

    try {
      setIsSaving(true);
      setSaveSuccess(false);

      await workoutAPI.saveMarathonCalculation({
        name,
        goal_marathon_time_seconds: results.inputs.goalTimeSeconds,
        age: results.inputs.age,
        calculation_data: results
      });

      setSaveSuccess(true);
      setSaveName('');
      await fetchSavedCalculations();

      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving calculation:', err);
      setError('Failed to save calculation');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle load saved calculation
  const handleLoad = (calculation) => {
    const data = calculation.calculation_data;
    setGoalTime(data.inputs.goalTimeString);
    setAge(data.inputs.age.toString());
    setResults(data);
    setError(null);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this saved calculation?')) return;

    try {
      await workoutAPI.deleteMarathonCalculation(id);
      await fetchSavedCalculations();
    } catch (err) {
      console.error('Error deleting calculation:', err);
      setError('Failed to delete calculation');
    }
  };

  // Handle set active
  const handleSetActive = async (id) => {
    try {
      await workoutAPI.setActiveMarathonCalculation(id);
      await fetchSavedCalculations();
    } catch (err) {
      console.error('Error setting active calculation:', err);
      setError('Failed to set active calculation');
    }
  };

  // Handle Enter key in inputs
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCalculate();
    }
  };

  return (
    <div className="pfitzinger-calculator">
      {/* Input Section */}
      <Card className="input-section mb-4">
        <Card.Body>
          <h5 className="mb-3">
            <i className="fas fa-running me-2 text-warning"></i>
            Pfitzinger Marathon Pace Calculator
          </h5>
          <p className="text-muted mb-3">
            Enter your goal marathon time and age to calculate training pace zones and heart rate zones.
          </p>

          <Row className="align-items-end">
            <Col xs={12} sm={4} className="mb-3 mb-sm-0">
              <Form.Group>
                <Form.Label>Goal Marathon Time</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="H:MM:SS (e.g., 3:30:00)"
                  value={goalTime}
                  onChange={(e) => setGoalTime(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
              </Form.Group>
            </Col>
            <Col xs={12} sm={3} className="mb-3 mb-sm-0">
              <Form.Group>
                <Form.Label>Age (years)</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="35"
                  min="10"
                  max="100"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
              </Form.Group>
            </Col>
            <Col xs={12} sm={5}>
              <Button
                variant="warning"
                onClick={handleCalculate}
                className="w-100"
                disabled={!goalTime || !age}
              >
                <i className="fas fa-calculator me-2"></i>
                Calculate Zones
              </Button>
            </Col>
          </Row>

          {error && (
            <Alert variant="danger" className="mt-3 mb-0">
              {error}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Results Section */}
      {results && (
        <Card className="results-section mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                <i className="fas fa-chart-line me-2 text-success"></i>
                Training Zones
              </h5>
              <div className="text-muted">
                Goal: <strong>{results.goalPace.display}</strong> | Max HR: <strong>{results.maxHR} bpm</strong>
              </div>
            </div>

            <Row>
              {/* Pace Zones Table */}
              <Col xs={12} lg={6} className="mb-3 mb-lg-0">
                <h6 className="text-center mb-2">
                  <i className="fas fa-stopwatch me-1"></i> Pace Zones
                </h6>
                <Table bordered hover size="sm" className="zone-table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '35%' }}>Zone</th>
                      <th>Pace Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RUN_TYPE_ORDER.map(({ key, name, abbrev }) => (
                      <tr key={key}>
                        <td className="zone-name">
                          {name} <span className="text-muted">({abbrev})</span>
                        </td>
                        <td className="zone-value">
                          {results.paceZones[key].display}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>

              {/* HR Zones Table */}
              <Col xs={12} lg={6}>
                <h6 className="text-center mb-2">
                  <i className="fas fa-heartbeat me-1"></i> Heart Rate Zones
                </h6>
                <Table bordered hover size="sm" className="zone-table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '35%' }}>Zone</th>
                      <th>HR Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RUN_TYPE_ORDER.map(({ key, name, abbrev }) => (
                      <tr key={key}>
                        <td className="zone-name">
                          {name} <span className="text-muted">({abbrev})</span>
                        </td>
                        <td className="zone-value">
                          {results.hrZones[key].display}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            </Row>

            {/* Save Section */}
            <hr className="my-3" />
            <Row className="align-items-center">
              <Col xs={12} sm={8} className="mb-2 mb-sm-0">
                <InputGroup>
                  <InputGroup.Text>
                    <i className="fas fa-save"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Name this calculation (optional)"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col xs={12} sm={4}>
                <Button
                  variant="success"
                  onClick={handleSave}
                  className="w-100"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Save Calculation
                    </>
                  )}
                </Button>
              </Col>
            </Row>

            {saveSuccess && (
              <Alert variant="success" className="mt-3 mb-0">
                <i className="fas fa-check-circle me-2"></i>
                Calculation saved successfully!
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Saved Calculations Section */}
      <Card className="saved-calculations">
        <Card.Body>
          <h5 className="mb-3">
            <i className="fas fa-folder-open me-2 text-primary"></i>
            Saved Calculations
          </h5>

          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-2 mb-0">Loading saved calculations...</p>
            </div>
          ) : loadError ? (
            <Alert variant="warning">{loadError}</Alert>
          ) : savedCalculations.length === 0 ? (
            <p className="text-muted mb-0">
              No saved calculations yet. Calculate your zones above and save them for future reference.
            </p>
          ) : (
            <div className="saved-list">
              {savedCalculations.map((calc) => (
                <Card
                  key={calc.id}
                  className={`calculation-card ${calc.is_active ? 'active' : ''}`}
                >
                  <Card.Body className="py-2 px-3">
                    <Row className="align-items-center">
                      <Col xs={12} sm={6} className="mb-2 mb-sm-0">
                        <div className="d-flex align-items-center">
                          {calc.is_active && (
                            <span className="badge bg-success me-2">Active</span>
                          )}
                          <strong>{calc.name || 'Unnamed'}</strong>
                        </div>
                        <small className="text-muted">
                          Goal: {formatMarathonTime(calc.goal_marathon_time_seconds)} | Age: {calc.age}
                        </small>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div className="d-flex justify-content-end gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleLoad(calc)}
                          >
                            <i className="fas fa-eye me-1"></i>
                            Load
                          </Button>
                          {!calc.is_active && (
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleSetActive(calc.id)}
                            >
                              <i className="fas fa-star me-1"></i>
                              Set Active
                            </Button>
                          )}
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(calc.id)}
                          >
                            <i className="fas fa-trash me-1"></i>
                            Delete
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default PfitzingerCalculator;
